import {
  useContext,
  MouseEvent,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react'
import { useForm } from 'react-hook-form'

import { SanitySimplePortableText } from '@data/sanity/queries/types/content'
import { CartContext } from '@lib/cart'
import { DiscountContext } from '@lib/discount'
import { triggerInitiateCheckoutFacebookEvent } from '@lib/facebook'
import { ErrorMessages } from '@lib/helpers'
import { InstitutionContext } from '@lib/institution-context'
import { LanguageContext, Locale } from '@lib/language'
import { PrintShopContext } from '@lib/print/print-shop-context'
import { ShopContext } from '@lib/shop-context'
import { SiteContext } from '@lib/site'
import { StringsContext } from '@lib/strings-context'

import Button from '@components/buttons/button'
import Checkbox from '@components/checkbox'
import SimplePortableText from '@components/portable-text/simple'

/**
 * Adds Google Analytics linker param to URL.
 */
const addLinkerParamToUrl = async (url: string) =>
  new Promise<string>((resolve) => {
    if (!window.ga) {
      resolve(url)
      return
    }

    window.ga(() => {
      const linkerParam = window.ga?.getAll()?.[0]?.get('linkerParam')
      const searchParams = new URLSearchParams(linkerParam)

      const newUrl = new URL(url)
      searchParams.forEach((value, key) => newUrl.searchParams.set(key, value))
      resolve(newUrl.toString())
    })
  })

/**
 * Adds locale param to URL.
 */
const addLocaleParamToUrl = (url: string, locale: Locale) => {
  const newUrl = new URL(url)
  newUrl.searchParams.set('locale', locale)

  return newUrl.toString()
}

/**
 * Formats checkout URL by replacing host and adding linker param.
 */
const formatCheckoutUrl = async (
  checkoutUrl: string,
  locale: Locale,
  storeUrl?: string,
) => {
  let formattedCheckoutUrl = `${checkoutUrl}`

  // Update checkout URL to use custom domain name
  if (storeUrl) {
    formattedCheckoutUrl = formattedCheckoutUrl.replace(
      /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/g,
      storeUrl,
    )
  }

  formattedCheckoutUrl = addLocaleParamToUrl(formattedCheckoutUrl, locale)
  formattedCheckoutUrl = await addLinkerParamToUrl(formattedCheckoutUrl)

  return formattedCheckoutUrl
}

interface CartSubmitProps {
  vatId: string
  comment: string
  setErrorMessages: Dispatch<SetStateAction<ErrorMessages>>
  storeUrl?: string
  terms?: SanitySimplePortableText
  className?: string
}

const CartSubmit = ({
  vatId,
  comment,
  setErrorMessages,
  storeUrl,
  terms,
  className,
}: CartSubmitProps) => {
  const {
    cart,
    cartLineItemInstitutionId,
    isCartUpdating,
    isCartSubmitting,
    submitCart,
    toggleCart,
  } = useContext(CartContext)
  const { cartDiscountItems } = useContext(DiscountContext)
  const { institutionId } = useContext(InstitutionContext)
  const { locale } = useContext(LanguageContext)
  const { printShopInstitutionId } = useContext(PrintShopContext)
  const { currencyCode } = useContext(ShopContext)
  const { settings } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const { register, watch } = useForm()

  const acceptTermsRegister = register('acceptTerms')
  const hasAgreed = watch('acceptTerms')

  const triggerFacebookEvent = useCallback(async () => {
    if (!settings?.facebookEvents) {
      return
    }

    await triggerInitiateCheckoutFacebookEvent(
      locale,
      cart,
      cartDiscountItems ?? [],
      currencyCode,
    )
  }, [cart, cartDiscountItems, currencyCode, locale, settings?.facebookEvents])

  // Handles redirection after cart submit
  const goToCheckout = useCallback(
    async (checkoutUrl: string) => {
      await triggerFacebookEvent()

      const formattedCheckoutUrl = await formatCheckoutUrl(
        checkoutUrl,
        locale,
        storeUrl,
      )

      // Redirect to checkout
      window.location.href = formattedCheckoutUrl
    },
    [locale, storeUrl, triggerFacebookEvent],
  )

  // Handles cart submit and validation
  const handleCartSubmit = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()

      const currentInstitutionId =
        cartLineItemInstitutionId ?? printShopInstitutionId ?? institutionId
      const { errors, checkoutUrl } = await submitCart({
        vatId,
        comment,
        institutionId: currentInstitutionId,
      })
      const errorList = Object.entries(errors)

      setErrorMessages((messages) => ({
        ...messages,
        vatId: errors.vatId,
        institutionId: errors.institutionId,
      }))

      if (!checkoutUrl || errorList.length > 0) {
        return
      }

      await goToCheckout(checkoutUrl)
      toggleCart(false)
    },
    [
      cartLineItemInstitutionId,
      comment,
      goToCheckout,
      institutionId,
      printShopInstitutionId,
      setErrorMessages,
      submitCart,
      toggleCart,
      vatId,
    ],
  )

  return (
    <div className={className}>
      {!!terms && (
        <Checkbox
          id={`cart-acceptTerms`}
          formRegister={acceptTermsRegister}
          className="mb-6 mx-auto"
        >
          <SimplePortableText content={terms} className="rc rc-checkbox" />
        </Checkbox>
      )}
      <div className="flex justify-center">
        <Button
          variant="primary"
          color="yellow"
          icon="ArrowRight"
          disabled={isCartUpdating || isCartSubmitting || (terms && !hasAgreed)}
          onClick={handleCartSubmit}
        >
          {isCartUpdating && strings.buttonUpdating}
          {isCartSubmitting && strings.buttonSubmitting}
          {!isCartUpdating && !isCartSubmitting && strings.cartSubmit}
        </Button>
      </div>
    </div>
  )
}

export default CartSubmit
